/*
 * ServicePartnerList.tsx
 * This Service Partner list Component is used for representing all the saved partners in list Form.
 */
// Import necessary modules and components
import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../components/page-header/page-header";
import GenericPopover from "../../../components/pop-over";
import useToast from "../../../hooks/useToast";
import Utils from "../../../utils";
import { TFilterModel } from "../../../types/config";
import { ePrivileges, eResultCode } from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { debounceTime, defaultFilterParams } from "../../../utils/constants";
import {
  Button,
  Drawer,
  Input,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import CustomDrawer from "../../../components/drawer";
import {
  AddEditIssueCategory,
  GetIssueCategoryList,
} from "../../../utils/api.constant";
import utils from "../../../utils";
import ActionCard from "../../../components/actionCard/actionCard";
import CustomModal from "../../../components/modal";
import { UserContext } from "../../../context";
import {
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  CloseOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import { TCategoryConfig } from "../../../types/index";
import ConfigCategoryForm from "./config-form";
import AddConfigCategoryForm from "./form";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../utils/date";

/**
 * ServicePartnerList Component
 * This component renders the list of service partners, with
 * the ability to search, filter, and add new service partners.
 * It also includes a bulk upload feature for importing
 * service partners in bulk.
 *
 * @returns {JSX.Element} The JSX structure for the
 *         ServicePartnerList component.
 */
export default function ConfigCategoryList() {
  // State and hooks initialization
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TCategoryConfig[]>([]);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const { onShowToast } = useToast();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPartner, setSelectedPartner] =
    useState<TCategoryConfig | null>(null);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  const initialColumns: any[] = [
    {
      title: "Issue Category Name",
      dataIndex: "issueCatName",
      key: "issueCatName",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "issueCatName"),
      sorter: (a: TCategoryConfig, b: TCategoryConfig) =>
        a.issueCatName.localeCompare(b.issueCatName),
      onFilter: (value: string | number | boolean, record: TCategoryConfig) =>
        record.issueCatName?.includes(value as string),
    },
    {
      title: "Sub Issue Category Name",
      dataIndex: "subIssueCatOneName",
      key: "subIssueCatOneName",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "subIssueCatOneName"),
      sorter: (a: TCategoryConfig, b: TCategoryConfig) =>
        a.subIssueCatOneName.localeCompare(b.subIssueCatOneName),
      onFilter: (value: string | number | boolean, record: TCategoryConfig) =>
        record.subIssueCatOneName?.includes(value as string),
    },
    {
      title: "Sub Issue Category Name 1",
      dataIndex: "subIssueCatTwoName",
      key: "subIssueCatTwoName",
      width: "15%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "subIssueCatTwoName"),
      sorter: (a: TCategoryConfig, b: TCategoryConfig) =>
        a.subIssueCatTwoName.localeCompare(b.subIssueCatTwoName),
      onFilter: (value: string | number | boolean, record: TCategoryConfig) =>
        record.subIssueCatTwoName?.includes(value as string),
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      width: "4%",
      render: (category: TCategoryConfig) => (
        <>
          <GenericPopover
            placement="left"
            content={
              <div className="standardActionBox">
                {/* Edit Row with CustomDrawer trigger */}
                {Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.EDITCONFIGCATEGORY
                ) ? (
                  <CustomDrawer
                    size="default"
                    trigger={
                      <ActionCard text="Edit">
                        <EditOutlined />
                      </ActionCard>
                    }
                    component={ConfigCategoryForm} // Passing ServicePartner component
                    componentProps={{
                      ...category,
                      onRefreshList: () => {
                        fetchData();
                      },
                    }}
                    title="Configure Category Form"
                  />
                ) : null}
                <ActionCard text={"Delete"} onClick={() => showModal(category)}>
                  <DeleteOutlined />
                </ActionCard>
              </div>
            }
            icon={
              <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
            }
          />
        </>
      ),
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState(
    defaultFilterParams.searchText
  );

  const context: any = useContext(UserContext);

  const updateCategoryDetails = async (values: TCategoryConfig) => {
    try {
      const { isDeleted, key, srNo, ...restValues } = values;
      const payload = {
        data: {
          ...restValues,
          isDeleted: isDeleted ? 1 : 0,
        },
      };
      console.log("payload", payload.data);
      const response = await post(AddEditIssueCategory, payload);
      console.log("response", response);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      console.log("Finally");
    }
  };

  useEffect(() => {
    fetchData();
  }, [debouncedSearchText]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const requestPayload = {
        data: {
          pageSize: -1,
          currentPage: 1,
        },
      };

      const response = await post(GetIssueCategoryList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map(
          (item: TCategoryConfig, index: number) => ({
            key: (item.id ?? "").toString(),
            srNo: index + 1,
            issueCatName: item.issueCatName,
            subIssueCatOneName: item.subIssueCatOneName,
            subIssueCatTwoName: item.subIssueCatTwoName,
            id: item.id,
            issueCatId: item.issueCatId,
            subIssueCatOneId: item.subIssueCatOneId,
            subIssueCatTwoId: item.subIssueCatTwoId,
          })
        );
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);
        updateColumnFilters(filteredData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  // Update search text state
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(filterParams.searchText);
    }, debounceTime); // Set debounce delay to 300 milliseconds

    return () => {
      clearTimeout(timer); // Clear the timer on each update
    };
  }, [filterParams.searchText]);

  const handleOk = async (categoryDetails: TCategoryConfig) => {
    if (categoryDetails) {
      await updateCategoryDetails(categoryDetails);
      fetchData();
    }
    setSelectedPartner(null);
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setSelectedPartner(null);
    setIsModalVisible(false);
  };

  const showModal = (category: TCategoryConfig) => {
    setSelectedPartner(category);
    setIsModalVisible(true);
  };

  /**
   * Updates the dealer with the given values.
   * Sends a request to the API with the given values, and if the response is successful, shows a success toast and fetches the data again.
   * If the response is not successful, shows an error toast with the description from the response.
   * @param {TDealer} values - the dealer values to update.
   */

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) => {
    const searchMatch = Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(searchText.toLowerCase())
    );

    if (!searchMatch) return false;
    const issueCatNameMatch =
      !activeFilters.issueCatName ||
      activeFilters.issueCatName.includes(item.issueCatName);

    const subIssueCatOneNameMatch =
      !activeFilters.subIssueCatOneName ||
      activeFilters.subIssueCatOneName.includes(item.subIssueCatOneName);

    const subIssueCatTwoNameMatch =
      !activeFilters.subIssueCatTwoName ||
      activeFilters.subIssueCatTwoName.includes(item.subIssueCatTwoName);

    return (
      searchMatch &&
      issueCatNameMatch &&
      subIssueCatOneNameMatch &&
      subIssueCatTwoNameMatch
    );
  });

  const updateColumnFilters = (dataset: TCategoryConfig[]) => {
    const issueCatNameFilters = Array.from(
      new Set(
        dataset
          .filter((item) => item.issueCatName)
          .map((item) => item.issueCatName)
      )
    ).map((issueCatName) => ({ text: issueCatName, value: issueCatName }));

    const subIssueCatOneNameFilters = Array.from(
      new Set(
        dataset
          .filter((item) => item.subIssueCatOneName)
          .map((item) => item.subIssueCatOneName)
      )
    ).map((subIssueCatOneName) => ({
      text: subIssueCatOneName,
      value: subIssueCatOneName,
    }));

    const subIssueCatTwoNameFilters = Array.from(
      new Set(
        dataset
          .filter((item) => item.subIssueCatTwoName)
          .map((item) => item.subIssueCatTwoName)
      )
    ).map((subIssueCatTwoName) => ({
      text: subIssueCatTwoName,
      value: subIssueCatTwoName,
    }));

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "issueCatName") {
          return { ...col, filters: issueCatNameFilters };
        }
        if (col.key === "subIssueCatOneName") {
          return { ...col, filters: subIssueCatOneNameFilters };
        }
        if (col.key === "subIssueCatTwoName") {
          return { ...col, filters: subIssueCatTwoNameFilters };
        }
        return col;
      })
    );
  };

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
  }));

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };
  // Return JSX structure for User Component
  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams?.totalRows} Total Category Configuration`}
        size="large"
      >
        <Space>
          {Utils.isUserHasAccess(
            context.user?.privilegeList,
            ePrivileges.ADDCONFIGCATEGORY
          ) ? (
            <>
              <CustomDrawer
                trigger={
                  <Button disabled={isLoading} type="primary">
                    Configure
                  </Button>
                }
                component={ConfigCategoryForm}
                componentProps={{
                  id: 0,
                  onRefreshList: () => {
                    fetchData();
                  },
                }}
                title="Configure Category Form"
                size="default"
              />

              <CustomDrawer
                size="default"
                trigger={
                  <Button disabled={isLoading} type="primary">
                    Add
                  </Button>
                }
                component={AddConfigCategoryForm} // Passing Service Partner Form component
                componentProps={{
                  id: 0, // Pass necessary props for ServicePartnerForm
                  onRefreshList: () => {
                    fetchData();
                  },
                }}
                title="Configure Category Form"
              />
            </>
          ) : null}

          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`ticket_issue_category_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
      <CustomModal
        title="Confirm"
        isOpen={isModalVisible} // Ensure selectedUserId is valid
        okText="Yes"
        cancelText="No"
        onOk={() =>
          handleOk({
            ...selectedPartner,
            isDeleted: true,
          } as any)
        }
        onCancel={() => handleCancel()}
      >
        <p>
          You are about to delete <b>{selectedPartner?.issueCatName}</b>
        </p>
      </CustomModal>
    </div>
  );
}
